/**
 * The session id stack every event carries.
 *
 * Tech solution §8.3 specifies `correlation jsonb -- session id stack, device
 * id`, and the consumer stores it verbatim. This module is where the stack
 * lives.
 *
 * Ambient, not an argument, because the stack describes where the user IS
 * rather than what is being tracked: the components that fire events sit
 * several levels below the pages that open sessions, and threading four ids
 * through every one of them would mean each new event site has to remember to
 * do it. Here, a page declares the sessions it owns once and every event fired
 * while that page is mounted is correlated - including events added later.
 *
 * The hierarchy being recorded (see current-event-activity-session.md §1.1):
 *
 *   active_sessions
 *    ├── student_lesson_session
 *    │     └── student_resource_session
 *    ├── student_resource_session
 *    ├── student_homework_session
 *    ├── student_project_session
 *    │     └── student_project_resource_session
 *    ├── teacher_lesson_session
 *    │     └── teacher_resource_session
 *    └── teacher_assessment_session
 *
 * `activeSessionId` is deliberately NOT settable here. It is stamped server
 * side from the auth cookie into `event_raw.session_id`, and identity is a
 * security boundary: a client-declared active session would let any user file
 * events against any session.
 */

/** Session ids are bigint server-side, so they are carried as strings. */
type SessionId = string | number | null | undefined;

export interface SessionCorrelation {
  /** student_lesson_session / teacher_lesson_session */
  lessonSessionId?: SessionId;
  /** student_resource_session / teacher_resource_session */
  resourceSessionId?: SessionId;
  /**
   * student_project_session. Distinct from lessonSessionId even though the
   * laboratory flow carries it in a URL parameter *named* lessonSessionId -
   * filing it as a lesson session would silently merge two different grains.
   */
  projectSessionId?: SessionId;
  /** student_project_resource_session */
  projectResourceSessionId?: SessionId;
  /** student_homework_session */
  homeworkSessionId?: SessionId;
  /** teacher_assessment_session */
  assessmentSessionId?: SessionId;
}

const KEYS: (keyof SessionCorrelation)[] = [
  'lessonSessionId',
  'resourceSessionId',
  'projectSessionId',
  'projectResourceSessionId',
  'homeworkSessionId',
  'assessmentSessionId',
];

let stack: Partial<Record<keyof SessionCorrelation, string>> = {};

/**
 * A session id that is absent is left OUT of the correlation rather than
 * written as null: "whatever is present" has to mean the key is missing when
 * the session is not open, so a projection can tell "no lesson session" from
 * "a lesson session whose id we lost".
 */
function normalise(value: SessionId): string | undefined {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  if (text === '' || text === 'undefined' || text === 'null') return undefined;
  // Number(...) of a bigint-sized id loses precision, so the guard is textual.
  return text === '0' ? undefined : text;
}

/**
 * Merges the ids a page owns into the stack. Passing null or undefined for a
 * key removes it, so closing a session is the same call as opening one.
 */
export function setSessionCorrelation(patch: SessionCorrelation): void {
  for (const key of KEYS) {
    if (!(key in patch)) continue;
    const value = normalise(patch[key]);
    if (value === undefined) {
      delete stack[key];
    } else {
      stack[key] = value;
    }
  }
}

/** Drops the named ids, or the whole stack when called with no argument. */
export function clearSessionCorrelation(
  keys?: (keyof SessionCorrelation)[],
): void {
  if (!keys) {
    stack = {};
    return;
  }
  for (const key of keys) {
    delete stack[key];
  }
}

/** A copy, so a caller cannot mutate the stack by holding on to the result. */
export function getSessionCorrelation(): Record<string, string> {
  return { ...stack };
}
